import React, { useState, useEffect } from "react";
import { votePurchaseService } from "../../services/votePurchaseService";

const VotePurchaseHistory = () => {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPurchaseHistory();
  }, []);

  const fetchPurchaseHistory = async () => {
    try {
      setLoading(true);
      const data = await votePurchaseService.getPurchaseHistory(20);
      setPurchases(Array.isArray(data) ? data : data?.data || []);
    } catch (err) {
      console.error("Error fetching purchase history:", err);
      setError("Failed to load purchase history");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "completed":
      case "successful":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100/20 text-green-300 border border-green-700/30">
            ✓ Completed
          </span>
        );
      case "pending":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100/20 text-yellow-300 border border-yellow-700/30">
            ⏳ Pending
          </span>
        );
      case "failed":
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100/20 text-red-300 border border-red-700/30">
            ✗ Failed
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100/20 text-gray-300">
            {status}
          </span>
        );
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Date unavailable";
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) return "Date unavailable";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getPackageName = (purchase) => {
    return (
      purchase.package?.name ||
      purchase.packageName ||
      purchase.name ||
      "Vote Package"
    );
  };

  const getAmount = (purchase) => {
    return purchase.amount ?? purchase.package?.price ?? purchase.price ?? 0;
  };

  const getVotes = (purchase) => {
    if (purchase.votesRemaining !== undefined && purchase.votesRemaining !== null) {
      return purchase.votesRemaining;
    }

    return (
      purchase.votes ??
      purchase.package?.votes ??
      purchase.votesPerDay ??
      purchase.package?.votesPerDay ??
      0
    );
  };

  const getStatus = (purchase) => {
    return purchase.status || (purchase.purchaseId ? "completed" : "pending");
  };

  if (loading) {
    return (
      <section role="status" aria-label="Loading purchase history" className="rounded-xl border border-line bg-surface-850 p-5 sm:p-6">
        <div className="flex min-h-28 items-center justify-center gap-3 text-sm text-gray-400">
          <span aria-hidden="true" className="h-5 w-5 animate-spin rounded-full border-2 border-line-strong border-t-accent" />
          Loading purchase history…
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section role="alert" className="rounded-xl border border-line bg-surface-850 p-5 sm:p-6">
        <p className="text-sm text-accent-soft">{error}</p>
      </section>
    );
  }

  if (purchases.length === 0) {
    return (
      <div className="rounded-xl border border-line bg-surface-850 px-5 py-9 text-center sm:p-10">
        <svg
          className="mx-auto mb-3 h-10 w-10 text-gray-600"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
          />
        </svg>
        <h3 className="text-base font-semibold text-white">No purchases yet</h3>
        <p className="mt-1 text-sm text-gray-400">Your vote package purchases will appear here.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-xl border border-line bg-surface-850 p-4 sm:p-5">
      <div className="max-w-full overflow-x-auto rounded-lg border border-line">
        <table className="w-full min-w-[640px] text-sm">
          <caption className="sr-only">Recent vote credit purchases and their status</caption>
          <thead className="bg-surface-900">
            <tr className="border-b border-line">
              <th scope="col" className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                Date
              </th>
              <th scope="col" className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                Package
              </th>
              <th scope="col" className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                Amount
              </th>
              <th scope="col" className="text-left px-3 py-3 text-xs font-semibold uppercase tracking-wider text-gray-400">
                Votes
              </th>
              <th scope="col" className="px-3 py-3 text-center text-xs font-semibold uppercase tracking-wider text-gray-400">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {purchases.map((purchase, index) => (
              <tr
                key={purchase.purchaseId || index}
                className="border-b border-line/50 transition-colors hover:bg-surface-800"
              >
                <td className="px-3 py-3 text-gray-300">
                  {formatDate(purchase.createdAt || purchase.date)}
                </td>
                <td className="px-3 py-3">
                  <span className="text-white font-medium">
                    {getPackageName(purchase)}
                  </span>
                </td>
                <td className="px-3 py-3 text-white">
                  ZMW {Number(getAmount(purchase)).toLocaleString()}
                </td>
                <td className="px-3 py-3">
                  <span className="font-semibold text-emerald-300">
                    +{Number(getVotes(purchase)).toLocaleString()}
                  </span>
                </td>
                <td className="px-3 py-3 text-center">
                  {getStatusBadge(getStatus(purchase))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VotePurchaseHistory;
